<script setup lang="ts">
import { computed, ref, onUnmounted, watch, type CSSProperties } from 'vue';
import { IonSpinner } from '@ionic/vue';
import { failedVariantUrls, rememberFailedVariant } from '@/utils/image-sources';

const emit = defineEmits(['load', 'loadStart']);

const { loading = 'lazy', ...props } = defineProps<{
  src?: string;
  // The original image to use when `src` is a pre-sized variant that fails.
  secondarySrc?: string;
  fallbackSrc?: string;
  isLoading?: boolean;
  height?: CSSProperties['height'];
  width?: CSSProperties['width'];
  loading?: HTMLImageElement['loading'];
  imgClass?: string;
}>();

// A freshly uploaded image can briefly miss on the CDN before its edge is warm.
// Retry remote images a few times (cache-busted) while KEEPING THE SPINNER UP so
// the broken <img> stays covered (no broken glyph, no placeholder flash), instead
// of falling to the fallback on the first transient miss. Use a short, flat delay
// so a cold image re-requests within the initial load window rather than resolving
// seconds later (which reads as a late "blink").
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 400;
const isImageLoading = ref(true);
const hasError = ref(false);
const retryCount = ref(0);
let retryTimer: ReturnType<typeof setTimeout> | null = null;

// A variant is written before any API response references it, so a failed
// variant is missing rather than cold: go straight to the original instead of
// retrying it, and skip it for the rest of the session.
const hasSecondary = computed(() => !!props.secondarySrc && props.secondarySrc !== props.src);
const startsOnSecondary = () => hasSecondary.value && !!props.src && failedVariantUrls.has(props.src);
const useSecondary = ref(startsOnSecondary());
const currentSrc = computed(() => (useSecondary.value ? props.secondarySrc : props.src));
const maxRetries = computed(() => (hasSecondary.value && !useSecondary.value ? 0 : MAX_RETRIES));

const isRemoteSrc = computed(
  () => !!currentSrc.value && !currentSrc.value.startsWith('data:') && !currentSrc.value.startsWith('blob:')
);

const src = computed(() => {
  const realSrc = hasError.value ? props.fallbackSrc : currentSrc.value || props.fallbackSrc;
  if (!realSrc || hasError.value || retryCount.value === 0 || !isRemoteSrc.value) return realSrc;
  return `${realSrc}${realSrc.includes('?') ? '&' : '?'}r=${retryCount.value}`;
});

const size = computed(() => ({
  h: typeof props.height === 'number' ? props.height + 'px' : props.height,
  w: typeof props.width === 'number' ? props.width + 'px' : props.width,
}));

const handleImageLoad = () => {
  isImageLoading.value = false;
  emit('load');
};

const handleImageError = () => {
  // Keep the spinner up (it covers the broken <img>) and retry transient CDN
  // misses before giving up and showing the fallback.
  if (isRemoteSrc.value && retryCount.value < maxRetries.value) {
    isImageLoading.value = true;
    const next = retryCount.value + 1;
    retryTimer = setTimeout(() => (retryCount.value = next), RETRY_DELAY_MS);
    return;
  }
  if (hasSecondary.value && !useSecondary.value) {
    if (props.src) rememberFailedVariant(props.src);
    isImageLoading.value = true;
    retryCount.value = 0;
    useSecondary.value = true;
    return;
  }
  hasError.value = true;
  isImageLoading.value = false;
  emit('load');
};

const handleImageLoadStart = () => {
  isImageLoading.value = true;
  hasError.value = false;
  emit('loadStart');
};

watch(
  () => [props.src, props.secondarySrc],
  () => {
    if (retryTimer) clearTimeout(retryTimer);
    retryCount.value = 0;
    hasError.value = false;
    useSecondary.value = startsOnSecondary();
    isImageLoading.value = true;
  }
);

onUnmounted(() => {
  if (retryTimer) clearTimeout(retryTimer);
  isImageLoading.value = false;
  hasError.value = false;
});
</script>

<template>
  <div :style="{ height: size.h, width: size.w }" class="base-img bg-dark">
    <div v-if="isLoading || isImageLoading" class="absolute inset-0 flex items-center justify-center z-10">
      <ion-spinner name="crescent" color="tertiary"></ion-spinner>
    </div>
    <img
      v-bind="$attrs"
      :src="src"
      :loading="loading"
      @load="handleImageLoad"
      @error="handleImageError"
      @loadstart="handleImageLoadStart"
      class="h-full w-full object-cover object-center"
      :class="imgClass"
    />
  </div>
</template>

<style lang="scss">
.base-img {
  position: relative;
}
</style>
